import React, { useMemo, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChevronDown, TrendingDown } from 'lucide-react';

import { DemoBanner } from './DemoBanner';

const chartData = [
  { date: '15 окт', anxiety: 62, progress: 58, depression: 22, emotional: 0 },
  { date: '22 окт', anxiety: 58, progress: 55, depression: 20, emotional: 0 },
  { date: '29 окт', anxiety: 60, progress: 57, depression: 18, emotional: 0 },
  { date: '5 ноя', anxiety: 55, progress: 60, depression: 20, emotional: 0 },
  { date: '12 ноя', anxiety: 52, progress: 64, depression: 18, emotional: 0 },
  { date: '19 ноя', anxiety: 58, progress: 62, depression: 22, emotional: 0 },
  { date: '26 ноя', anxiety: 56, progress: 58, depression: 24, emotional: 0 },
];

function CircleProgress({
  value,
  max,
  label,
  color,
}: {
  value: number;
  max: number;
  label: string;
  color: string;
}) {
  const pct = (value / max) * 100;
  const r = 36;
  const c = 2 * Math.PI * r;

  return (
    <div className="flex items-center gap-4">
      <div className="relative w-20 h-20">
        <svg width="80" height="80" viewBox="0 0 80 80">
          <circle cx="40" cy="40" r={r} fill="none" stroke="#e5e7eb" strokeWidth="8" />
          <circle
            cx="40"
            cy="40"
            r={r}
            fill="none"
            stroke={color}
            strokeWidth="8"
            strokeDasharray={c}
            strokeDashoffset={c - (pct / 100) * c}
            strokeLinecap="round"
            transform="rotate(-90 40 40)"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-sm font-bold" style={{ color }}>
            {value}/{max}
          </span>
        </div>
      </div>
      <div>
        <p className="font-semibold text-sm" style={{ color }}>
          {label}
        </p>
      </div>
    </div>
  );
}

export function Progress() {
  const [calibrationOpen, setCalibrationOpen] = useState(true);

  const calibrationItems = useMemo(
    () => ({
      score: '58%',
      hint: 'Взвешенное среднее по всем показателям',
    }),
    [],
  );

  return (
    <div className="space-y-4">
      <DemoBanner section="Прогресс" />

      <button
        type="button"
        className="bg-white border border-gray-200 rounded-xl p-4 flex items-start justify-between cursor-pointer hover:bg-gray-50 w-full text-left"
        onClick={() => setCalibrationOpen((v) => !v)}
      >
        <div>
          <p className="font-semibold text-gray-900 text-sm">Калибровка шкал PHQ-9 / GAD-7</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Введите клинически оцениваемые или самооценочные данные опросников для отслеживания результатов
          </p>
          {calibrationOpen ? (
            <p className="text-xs text-gray-400 mt-2">Данные для расчёта обновляются после каждой сессии (замокано).</p>
          ) : null}
        </div>
        <ChevronDown size={16} className="text-gray-400 mt-1" />
      </button>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <p className="text-sm text-gray-600 mb-2">Оценка клинического прогресса</p>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-3xl font-bold text-amber-500">{calibrationItems.score}</span>
            <TrendingDown size={18} className="text-[#C41230]" />
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
            <div className="bg-amber-400 h-2.5 rounded-full" style={{ width: '58%' }} />
          </div>
          <p className="text-xs text-gray-400">{calibrationItems.hint}</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <p className="text-sm text-gray-600 mb-3">Ключевые моменты последней сессии</p>
          <p className="text-xs text-gray-400 mb-4">Нет ключевых моментов для этой сессии</p>
          <button type="button" className="w-full border border-gray-200 rounded-lg py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
            Просмотреть детали сессии
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-amber-50 border border-amber-100 rounded-xl p-5">
          <p className="text-xs text-gray-500 mb-3 font-medium">Депрессия</p>
          <CircleProgress value={7} max={27} label="Мягкая" color="#f59e0b" />
          <p className="text-xs text-gray-500 mt-2">PHQ-9 · Высокие значения указывают на ухудшение симптомов</p>
        </div>

        <div className="bg-amber-50 border border-amber-100 rounded-xl p-5">
          <p className="text-xs text-gray-500 mb-3 font-medium">Тревога</p>
          <CircleProgress value={13} max={21} label="Умеренная" color="#f59e0b" />
          <p className="text-xs text-gray-500 mt-2">GAD-7 · Высокие значения указывают на ухудшение симптомов</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {['Эмоциональная регуляция', 'Достижение целей', 'Функциональное улучшение'].map((label) => (
          <div key={label} className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="text-xs text-gray-500 mb-2">{label}</p>
            <p className="text-sm text-gray-400">Недостаточно данных для расчёта</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h4 className="font-semibold text-gray-900 mb-4">Динамика клинического прогресса</h4>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: '10px' }} />
              <Line type="monotone" dataKey="anxiety" stroke="#9b5cf6" dot={{ r: 3 }} name="Тревога" />
              <Line type="monotone" dataKey="progress" stroke="#3b82f6" dot={{ r: 3 }} name="Оценка прогресса" />
              <Line type="monotone" dataKey="depression" stroke="#ef4444" dot={{ r: 3 }} name="Депрессия" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-gray-900">Резюме сессии</h4>
            <span className="text-xs text-gray-400">26 ноября 2025</span>
          </div>

          <div className="space-y-2">
            {[
              { label: '58% Частичное клиническое улучшение', color: 'text-amber-500' },
              { label: '26% Мягкая депрессия · 7/27·PHQ-9', color: 'text-amber-500' },
              { label: '62% Умеренная тревога · 13/21·GAD-7', color: 'text-amber-500' },
              { label: 'Достижение целей', color: 'text-gray-700' },
              { label: 'Эмоциональная регуляция', color: 'text-gray-700' },
            ].map((item) => (
              <div key={item.label} className="flex items-center justify-between border-b border-gray-100 pb-2">
                <span className={`text-sm font-medium ${item.color}`}>{item.label}</span>
                <ChevronDown size={14} className="text-gray-400" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

